import { useState } from "react";
import type { GroupView } from "@/services/types";
import { groupService } from "@/services";
import { useToast } from "@/context/ToastContext";
import { Card } from "@/components/base/Card";
import { Button } from "@/components/base/Button";
import { TextField } from "@/components/base/TextField";
import { CurrencyInput } from "@/components/currency/CurrencyInput";
import { Toggle } from "@/components/base/Toggle";
import { Badge } from "@/components/base/Badge";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { formatDate } from "@/utils/format";

const toInt = (v: string) => (v.trim() === "" ? 0 : parseInt(v, 10));

interface SettingsTabProps {
  group: GroupView;
  isViewer: boolean;
  onGroupUpdated: (g: GroupView) => void;
}

function LockedRow({ label, value, helper }: { label: string; value: React.ReactNode; helper?: string }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2">
      <div className="flex items-center gap-2">
        <i className="ri-lock-2-line text-foreground-300" />
        <div>
          <p className="text-xs text-foreground-500">{label}</p>
          {helper ? <p className="text-[11px] text-foreground-400">{helper}</p> : null}
        </div>
      </div>
      <span className="text-right text-sm font-medium text-foreground-900">{value}</span>
    </div>
  );
}

export function SettingsTab({ group, isViewer, onGroupUpdated }: SettingsTabProps) {
  const toast = useToast();
  const locked = group.status === "completed" || group.status === "cancelled";

  const [grace, setGrace] = useState(String(group.gracePeriodDays));
  const [lateFee, setLateFee] = useState<number | null>(group.lateFee);
  const [feeIsPercent, setFeeIsPercent] = useState(group.lateFeeIsPercent);
  const [feePerDay, setFeePerDay] = useState(group.lateFeePerDay);
  const [minDisc, setMinDisc] = useState(group.minDiscountPct != null ? String(group.minDiscountPct) : "");
  const [maxDisc, setMaxDisc] = useState(group.maxDiscountPct != null ? String(group.maxDiscountPct) : "");
  const [blockOverdue, setBlockOverdue] = useState(group.blockOverdueBidding);
  const [saving, setSaving] = useState(false);

  const dirty =
    grace !== String(group.gracePeriodDays) ||
    lateFee !== group.lateFee ||
    feeIsPercent !== group.lateFeeIsPercent ||
    feePerDay !== group.lateFeePerDay ||
    minDisc !== (group.minDiscountPct != null ? String(group.minDiscountPct) : "") ||
    maxDisc !== (group.maxDiscountPct != null ? String(group.maxDiscountPct) : "") ||
    blockOverdue !== group.blockOverdueBidding;

  const save = async () => {
    if (isViewer) return;
    setSaving(true);
    try {
      const updated = await groupService.updatePolicies(
        group.id,
        {
          gracePeriodDays: toInt(grace),
          lateFee: lateFee ?? 0,
          lateFeeIsPercent: feeIsPercent,
          lateFeePerDay: feePerDay,
          minDiscountPct: minDisc.trim() === "" ? null : parseFloat(minDisc),
          maxDiscountPct: maxDisc.trim() === "" ? null : parseFloat(maxDisc),
          blockOverdueBidding: blockOverdue,
        },
        "Staff User"
      );
      toast.success("Policies updated", "Operational settings saved.");
      onGroupUpdated(updated);
    } catch (err) {
      toast.error("Could not save policies", err instanceof Error ? err.message : "Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card
        title="Structural configuration"
        subtitle="These values are arithmetic and lock permanently once the group is active."
        actions={<Badge variant="pending" dot>Locked</Badge>}
      >
        <div className="grid grid-cols-1 gap-x-8 md:grid-cols-2">
          <LockedRow label="Monthly installment" value={<CurrencyDisplay amount={group.monthlyInstallment} hideSymbol />} />
          <LockedRow label="Member count / duration" value={`${group.memberCount} members · ${group.memberCount} months`} />
          <LockedRow label="Chit value" value={<CurrencyDisplay amount={group.chitValue} hideSymbol />} />
          <LockedRow label="Reserved organizer cycle" value={`Cycle ${group.reservedCycle}`} />
          <LockedRow label="Start date" value={formatDate(group.startDate)} />
          <LockedRow label="Auction / due day" value={`${group.auctionDay}th / ${group.dueDay}th`} />
          <LockedRow label="Commission" value={group.commissionType === "none" ? "None" : `${group.commissionType} · ${group.commissionValue}${group.commissionType === "fixed" ? "" : "%"}`} />
        </div>
        <p className="mt-3 rounded-md bg-background-100/60 px-3 py-2 text-xs text-foreground-600">
          Changing any of these after activation would corrupt every winning-bid, dividend and installment calculation that follows.
        </p>
      </Card>

      <Card
        title="Operational policies"
        subtitle="Grace period, late fee, bidding caps and the overdue block stay editable — these are policy, not arithmetic."
        actions={locked ? <Badge variant="inactive">Read-only</Badge> : null}
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <TextField label="Grace period (days)" inputMode="numeric" value={grace} onChange={(e) => setGrace(e.target.value)} disabled={isViewer || locked} />
          <CurrencyInput label="Late fee" value={lateFee} onChange={setLateFee} disabled={isViewer || locked} />
          <div className="flex flex-col justify-end gap-3">
            <Toggle checked={feeIsPercent} onChange={setFeeIsPercent} label="Fee is a percentage" size="sm" disabled={isViewer || locked} />
            <Toggle checked={feePerDay} onChange={setFeePerDay} label="Accrues per day" size="sm" disabled={isViewer || locked} />
          </div>
        </div>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <TextField label="Min discount (%)" inputMode="decimal" value={minDisc} onChange={(e) => setMinDisc(e.target.value)} disabled={isViewer || locked} placeholder="No cap" />
          <TextField label="Max discount (%)" inputMode="decimal" value={maxDisc} onChange={(e) => setMaxDisc(e.target.value)} disabled={isViewer || locked} placeholder="No cap" />
          <div className="flex items-end">
            <Toggle checked={blockOverdue} onChange={setBlockOverdue} label="Block overdue bidders" size="sm" disabled={isViewer || locked} />
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" disabled={isViewer || locked || !dirty} onClick={() => { setGrace(String(group.gracePeriodDays)); setLateFee(group.lateFee); setFeeIsPercent(group.lateFeeIsPercent); setFeePerDay(group.lateFeePerDay); setMinDisc(group.minDiscountPct != null ? String(group.minDiscountPct) : ""); setMaxDisc(group.maxDiscountPct != null ? String(group.maxDiscountPct) : ""); setBlockOverdue(group.blockOverdueBidding); }}>
            Reset
          </Button>
          <Button icon={<i className="ri-check-line" />} loading={saving} disabled={isViewer || locked || !dirty} onClick={save}>
            Save policies
          </Button>
        </div>
      </Card>
    </div>
  );
}